import { ILCHttpResponse } from '@logicalcommander/types'
import { createApi } from '@reduxjs/toolkit/query/react'
import { v4 as uuidv4 } from 'uuid'
import { configManager } from '../../config/configManager'
import {
  ICustomReport,
  IDefaultReport,
  IReportConfig,
  IReportGenerationResponse,
  IReportRequest,
  IReportStatusResponse,
  ISaveReportRequest,
} from '@logicalcommander/types'
import { RootState } from '../redux/store'
import { createBaseQueryWithErrorHandling } from './baseQueryWithErrorHandling'

export interface IGenerateReportRequest extends IReportRequest {
  utcOffset?: string
  identity?: string
  evaluationId?: string
  caseId?: number
  reportName?: string
  aiReport?: boolean
  myDashboardExport?: boolean
  dashboardPeriod?: 'week' | 'month' | 'sixMonths' | 'year' | 'allTime'
  dashboardStartDate?: string
  dashboardEndDate?: string
}

export const reportsApi = createApi({
  reducerPath: 'reportsApi',
  baseQuery: createBaseQueryWithErrorHandling(`${configManager.api.GATEWAY_URL}/reports`, (headers, { getState }) => {
    headers.set('x-request-id', uuidv4())
    const { token, companyToken } = (getState() as RootState).authReducer
    if (token) {
      headers.set('authorization', `Bearer ${token}`)
    }
    if (companyToken) {
      headers.set('x-company-token', companyToken)
    }
    return headers
  }),
  tagTypes: ['Report'],
  endpoints: (builder) => ({
    getReportConfig: builder.query<IReportConfig, void>({
      query: () => ({
        url: '/config',
        method: 'GET',
      }),
      transformResponse: (response: ILCHttpResponse<IReportConfig>) => {
        return response.data as IReportConfig
      },
    }),
    getDefaultReports: builder.query<IDefaultReport[], void>({
      query: () => ({
        url: '/default',
        method: 'GET',
      }),
      transformResponse: (response: ILCHttpResponse<IDefaultReport[]>) => {
        return response.data as IDefaultReport[]
      },
    }),
    getSavedReports: builder.query<ICustomReport[], void>({
      query: () => ({
        url: '/saved',
        method: 'GET',
      }),
      providesTags: ['Report'],
      transformResponse: (response: ILCHttpResponse<ICustomReport[]>) => {
        return response.data as ICustomReport[]
      },
    }),
    generateReport: builder.mutation<IReportGenerationResponse, IGenerateReportRequest>({
      query: (reportRequest) => ({
        url: '/generate',
        method: 'POST',
        body: reportRequest,
      }),
      invalidatesTags: ['Report'],
      transformResponse: (response: ILCHttpResponse<IReportGenerationResponse>) => {
        return response.data as IReportGenerationResponse
      },
    }),
    getReportStatus: builder.query<IReportStatusResponse, string>({
      query: (jobId) => ({
        url: `/status/${jobId}`,
        method: 'GET',
      }),
      transformResponse: (response: ILCHttpResponse<IReportStatusResponse>) => {
        return response.data as IReportStatusResponse
      },
    }),
    getGeneratedReports: builder.query<ICustomReport[], void>({
      query: () => ({
        url: '/generated',
        method: 'GET',
      }),
      providesTags: ['Report'],
      transformResponse: (response: ILCHttpResponse<ICustomReport[]>) => {
        return response.data as ICustomReport[]
      },
    }),
    downloadReportFile: builder.query<Blob, string>({
      query: (jobId) => ({
        url: `/download/${jobId}/file`,
        method: 'GET',
        responseHandler: (response: Response) => response.blob(),
      }),
    }),
    getReportDownloadUrl: builder.query<{ downloadUrl: string }, string>({
      query: (jobId) => ({
        url: `/download/${jobId}`,
        method: 'GET',
      }),
      transformResponse: (response: ILCHttpResponse<{ downloadUrl: string }>) => {
        return response.data || { downloadUrl: '' }
      },
    }),
    retryStuckReport: builder.mutation<void, string>({
      query: (jobId) => ({
        url: `/generated/${jobId}/retry`,
        method: 'POST',
      }),
      invalidatesTags: ['Report'],
    }),
    deleteGeneratedReport: builder.mutation<void, string>({
      query: (jobId) => ({
        url: `/generated/${jobId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Report'],
    }),
    updateGeneratedReportName: builder.mutation<ICustomReport, { jobId: string; name: string }>({
      query: ({ jobId, name }) => ({
        url: `/generated/${jobId}/name`,
        method: 'PATCH',
        body: { name },
      }),
      invalidatesTags: ['Report'],
      transformResponse: (response: ILCHttpResponse<ICustomReport>) => {
        return response.data as ICustomReport
      },
    }),
    saveReport: builder.mutation<ICustomReport, ISaveReportRequest>({
      query: (saveReportRequest) => ({
        url: '/save',
        method: 'POST',
        body: saveReportRequest,
      }),
      invalidatesTags: ['Report'],
      transformResponse: (response: ILCHttpResponse<ICustomReport>) => {
        return response.data as ICustomReport
      },
    }),
    deleteReport: builder.mutation<void, string>({
      query: (reportId) => ({
        url: `/${reportId}`,
        method: 'DELETE',
      }),
      invalidatesTags: ['Report'],
    }),
  }),
})

export const {
  useGetReportConfigQuery,
  useGetDefaultReportsQuery,
  useGetSavedReportsQuery,
  useGenerateReportMutation,
  useGetReportStatusQuery,
  useGetGeneratedReportsQuery,
  useGetReportDownloadUrlQuery,
  useLazyGetReportDownloadUrlQuery,
  useLazyDownloadReportFileQuery,
  useRetryStuckReportMutation,
  useDeleteGeneratedReportMutation,
  useUpdateGeneratedReportNameMutation,
  useSaveReportMutation,
  useDeleteReportMutation,
} = reportsApi
